import { profile } from '@/configs/endpoints'
import api from '@/plugins/api'
import requestValidation from '@/configs/requestValidation'
import { initPermissions, access } from '@/configs/permissions'
import { generateFilterParams } from '@/configs/helpers'

const store = 'profile'

export default {
  state: () => ({
    profile: {},
    counters: {},
    sessions: {},
    signatureKeys: [],
    isLoadSessions: false,
    isLoadSignatureKeys: false,

    // used for the lab if moderation is not yet complete
    isActiveProfile: false,

    //TODO: for testing profiles
    isShowTestButton: false,

    passwordUpdateError: []
  }),
  getters: {
    isPremiumAccount: state => state.profile.institution_type === 1 &&
      (state.profile.userprofile?.can_payment || state.profile.type_user === 'Guest'),

    isReprocessing: state => {
      let { certification_statement, laboratory_statement } = state.profile
      let organization = certification_statement && Object.keys(certification_statement).length
        ? 'OC'
        : laboratory_statement && Object.keys(laboratory_statement).length
          ? 'VL'
          : null

      return !!(organization &&
        (certification_statement?.statement_step === 11 || laboratory_statement?.statement_step === 11))
    },
    isReprocessingOrganizationType: state => {
      let { certification_statement, laboratory_statement } = state.profile
      return certification_statement && Object.keys(certification_statement).length
        ? 'OC'
        : laboratory_statement && Object.keys(laboratory_statement).length
          ? 'VL'
          : null
    },
    getCounters: state => state.counters
  },
  actions: {
    setAuth (context, res) {
      if (res.data.token) {
        localStorage.removeItem('U2FData')
        localStorage.removeItem('TOKENU2F')
        localStorage.removeItem('TOKENTOTP')
        localStorage.removeItem('TOKENL')
        localStorage.removeItem('TOKEN_MEASUREMENT')
        document.cookie = 'TOKENU2F=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        document.cookie = 'TOKENTOTP=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        document.cookie = 'TOKEN_MEASUREMENT=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        localStorage.setItem('TOKEN', `Token ${ res.data.token }`)
        document.cookie = `TOKEN=Token ${ res.data.token }; samesite=strict; max-age=28800`
        if (res.data.additional_creds?.token) {
          localStorage.setItem('TOKEN_MEASUREMENT', `Bearer ${ res.data.additional_creds.token }`)
        }
        location = '/'
      }
    },
    async signIn (context, body) {
      const response = await api.post(profile.signIn, {body: body})
      if (await requestValidation(response)) await context.dispatch('setAuth', response)
    },
    async signQR (context, code) {
      const response = await api.get(profile.getTokenByQR + code)
      if (await requestValidation(response)) await context.dispatch('setAuth', response)
    },
    async getProfile (context) {
      const response = await api.get(profile.me)
      if (await requestValidation(response)) {
        context.commit('SET_DATA', { store, state: 'profile', data: response.data })
        context.commit('SET_DATA', { store, state: 'isActiveProfile', data: response.data.is_active_profile })

        window.chatwootSDK?.run({
          websiteToken: process.env.VUE_APP_CHATWOOT_TOKEN,
          baseUrl: process.env.VUE_APP_CHATWOOT_BASE_URL
        })

        let { email, phone_number, first_name, last_name, middle_name, userprofile, type_user} = response.data

        let contact = {
          email: email,
          name: `${last_name} ${first_name} ${middle_name}`,
          phone_number: phone_number,
          company_name: 'Guest',
          description: ''
        }

        if (userprofile) {
          let company = await context.dispatch('getOrganizationInfo', {userprofile, type_user})
          Object.assign(contact, company)
        }

        window.addEventListener("chatwoot:ready", () => {
          window.$chatwoot.setUser(id, contact)
        })

        //TODO: for testing profiles
        context.commit('SET_DATA', {
          store,
          state: 'isShowTestButton',
          data: [15, 5, 10, 9, 14, 28, 72, 296, 320, 271, 5105, 5106].includes(response.data.userprofile?.id) &&
            (!!process.env.VUE_APP_MODE_STG || !!process.env.VUE_APP_MODE_TEST)
        })
        initPermissions()
        if (!access('guestUser')) await context.dispatch('getSignatureKeys')
        await context.dispatch('getCounter')
      }
    },

    async changePassword (context, body) {
      const response = await api.post(profile.changePassword, { body: body })
      if (response.code === 400) {
        context.dispatch('setPasswordUpdateError', response)
      }
      return response.data
    },
    async sendRecoveryPasswordTokenToEmail (context, body) {
      const response = await api.post(profile.sendRecoveryPasswordTokenToEmail, { body: body })
      if (response.code === 404) {
        context.dispatch('setPasswordUpdateError', response)
      }
      return response.data
    },
    async recoveryPasswordByToken (context, body) {
      const response = await api.post(profile.recoveryPasswordByToken, { body: body })

      if (response.code === 400) {
        context.dispatch('setPasswordUpdateError', response)
      }
      return response.data
    },

    async setPasswordUpdateError (context, response) {
      context.dispatch('clearPasswordUpdateError')
      let error = await response.data.json()
      for (let field in error) {
        let label = ''
        if (typeof error[field] !== 'string') {
          error[field].forEach(e => {
            label = `changePassword.${e.replace(/ /g, '_').replace('.', '')}`
          })
        } else {
          label = `changePassword.${error[field].replace(/ /g, '_').replace('.', '')}`
        }

        context.commit('ADD_DATA', {
          store,
          state: 'passwordUpdateError',
          data: label
        })
      }
    },

    async clearPasswordUpdateError (context) {
      context.commit('SET_DATA', { store, state: 'passwordUpdateError', data: [] })
    },

    async resetPassword (context, body) {
      const response = await api.post(profile.resetPassword, { body: body })
      return response.data
    },

    async getOrganizationInfo (context, user) {
      let data = {}
      let organizationType = ''
      if (Number.isInteger(user.userprofile.certification_agency)) {
        data = { endpoint: 'agency', id: user.userprofile.certification_agency }
        organizationType = 'ОС'
      }
      if (Number.isInteger(user.userprofile.measuring_laboratory)) {
        data = { endpoint: 'laboratory', id: user.userprofile.measuring_laboratory }
        organizationType = 'ВЛ'
      }

      let company = await context.dispatch('getDirectoryById', data)
      return {
        company_name: `${organizationType}: ${company.short_name}, ${user.type_user}`,
        description: `Назва: ${company.name} | EDPROU: ${company.EDPROU} | ID: ${company.id} | Підписка: ${company.institution_type}`
      }
    },

    async updateProfile (context, body) {
      let profileEndpoint = context.getters.isReprocessing
        ? profile.meGuest
        : profile.me

      const response = await api.patch(profileEndpoint, { body: body })
      if (await requestValidation(response)) {
        context.dispatch('getProfile')
      }
      return await requestValidation(response)
    },
    async setDocumentsProfile (context, files) {
      return Promise.all(files.map(async item => {
        const body = new FormData()
        body.append('type_file', item.type)

        item.files.forEach(file => {
          body.append('files', file)
        })
        return await api.post(profile.profileDocuments, { body: body })
      })).then(response => response.every(async r => await requestValidation(r)))
    },
    async updateDocumentsProfile (context, files) {
      let profileEndpoint = context.getters.isReprocessing
        ? profile.updateDocumentsGuestProfile
        : profile.updateDocumentsProfile

      return Promise.all(files.map(async item => {
        const body = new FormData()
        body.append('type_file', item.type)

        item.files.forEach(file => {
          body.append('files', file)
        })
        return await api.put(profileEndpoint, { body: body })
      })).then(response => response.every(async r => await requestValidation(r)))
    },
    async removeDocumentProfile (context, id) {
      let profileEndpoint = context.getters.isReprocessing
        ? profile.removeDocumentGuestProfile
        : profile.removeDocumentProfile

      const response = await api.delete(profileEndpoint(id))
      return await requestValidation(response)
    },
    async getSessions (context, filter) {
      context.commit('SET_DATA', { store, state: 'isLoadSessions', data: true })

      const response = await api.get(profile.sessions + generateFilterParams(filter) )
      if (await requestValidation(response)) {
        context.commit('SET_DATA', { store, state: 'sessions', data: response.data })
      }
      context.commit('SET_DATA', { store, state: 'isLoadSessions', data: false })
    },
    async deleteToken (context, id) {
      const response = await api.delete(`${ profile.sessions }${ id }/`)
      if (await requestValidation(response)) {
        context.commit('DELETE_DATA_TOKEN', { store, state: 'sessions', data: id })
      }
    },

    async getSignatureKeys (context) {
      context.commit('SET_DATA', { store, state: 'isLoadSignatureKeys', data: true })
      const response = await api.get(profile.signature)

      if (await requestValidation(response)) {
        const keys = response.data.results.filter(key => key.is_actual)
        context.commit('SET_DATA', { store, state: 'signatureKeys', data: keys })
      }
      context.commit('SET_DATA', { store, state: 'isLoadSignatureKeys', data: false })
    },

    async setSignatureKey (context, data) {
      const body = new FormData()
      body.append('password', data.body.password)
      body.append('file_signature', data.body.key)
      body.append('type_signature', data.body.type)
      body.append('EDRPOU', data.data.subjEDRPOUCode)
      body.append('name',  data.body.type === 'ST' ? data.data.subjOrg : data.data.subjFullName)

      const response = await api.post(profile.signature, { body: body, requestType: 'formData', responseType: 'json' })
      const isResult = await requestValidation(response)
      if (isResult) await context.dispatch('getSignatureKeys')
      return isResult
    },

    async deleteSignature (context, id) {
      const response = await api.delete(profile.deleteSignature(id))
      if (await requestValidation(response)) {
        await context.dispatch('getSignatureKeys')
        return response.data
      }
      return false
    },

    async getQR () {
      const response = await api.get(profile.qr)
      if (await requestValidation(response)) return response.data.qr_image_svg
    },

    async getCounter (context) {
      const response = await api.get(profile.counter)
      if (await requestValidation(response)) context.commit('SET_DATA', { store, state: 'counters', data: response.data })
    }
  }
}
